import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  GlobeAltIcon,
  MegaphoneIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";

const stats = [
  {
    icon: (
      <UserGroupIcon className="w-14 h-14 md:w-16 md:h-16 text-orange-500 dark:text-orange-400" />
    ),

    value: 50000,
    suffix: "+",
    label: "Global Content Creator",
  },
  {
    icon: (
      <MegaphoneIcon className="w-14 h-14 md:w-16 md:h-16 text-orange-500 dark:text-orange-400" />
    ),
    value: 500,
    suffix: "+",
    label: "Globally Launched Campaign",
  },
  {
    icon: (
      <GlobeAltIcon className="w-14 h-14 md:w-16 md:h-16 text-orange-500 dark:text-orange-400" />
    ),

    value: 5,
    suffix: "+",
    label: "Active Markets",
  },
];

const StatsSection = () => {
  const navigate = useNavigate();
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const [counters, setCounters] = useState(
    stats.map(() => 0)
  );

  useEffect(() => {
    if (inView) {
      const duration = 2000; // 2 seconds
      const steps = 60; // 60 steps for smooth animation
      const stepDuration = duration / steps;

      let currentStep = 0;
      const interval = setInterval(() => {
        if (currentStep >= steps) {
          setCounters(stats.map((stat) => stat.value)); // Ensure final value is exact
          clearInterval(interval);
          return;
        }

        setCounters((prevCounters) =>
          prevCounters.map((counter, index) => {
            const targetValue = stats[index].value;
            const increment = targetValue / steps;
            return Math.min(
              Math.round(counter + increment),
              targetValue
            );
          })
        );

        currentStep++;
      }, stepDuration);

      return () => clearInterval(interval);
    }
  }, [inView]);

  // Custom click handlers for each stat
  const handleStatClick = (value: number) => {
    if (value === 50000) {
      window.open("https://bookingkols.com.vn", "_blank");
    } else if (value === 500) {
      navigate("/cases");
    } else if (value === 5) {
      const globalPresence = document.getElementById(
        "global-presence-section"
      );
      if (globalPresence) {
        globalPresence.scrollIntoView({
          behavior: "smooth",
        });
      }
    }
  };

  return (
    <section className="w-full bg-transparent dark:bg-black py-10 md:py-16 transition-colors duration-300">
      <div
        ref={ref}
        className="max-w-6xl mx-auto px-4 flex flex-col sm:flex-row flex-wrap justify-between items-center gap-8 sm:gap-0"
      >
        {stats.map((stat, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 20 }}
            animate={
              inView
                ? { opacity: 1, y: 0 }
                : { opacity: 0, y: 20 }
            }
            transition={{ duration: 0.5, delay: idx * 0.2 }}
            onClick={() => handleStatClick(stat.value)}
            className="flex flex-col items-center text-center flex-1 min-w-[160px] max-w-xs bg-white dark:bg-neutral-800 rounded-xl shadow-md dark:shadow-lg py-6 px-4 transition-colors duration-300 cursor-pointer hover:shadow-lg hover:scale-105 transform transition-all duration-300"
          >
            <div className="mb-4">{stat.icon}</div>
            <div className="text-2xl md:text-3xl font-extrabold text-gray-900 dark:text-white mb-1">
              {counters[idx].toLocaleString()}
              {stat.suffix}
            </div>
            <div className="text-base md:text-lg text-gray-700 dark:text-gray-300">
              {stat.label}
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default StatsSection;
